import { useLocale } from '../../../../providers/UIProvider';
import { useCountry } from '../../../../providers/ReferenceDataProvider';
import type { FormatterProps } from '../types/editor-config';
import { ReactNode } from 'react';
import { FormatterError } from './FormatterError';

/**
 * Formats an ISO 3166-1 alpha-2 country code as `"CZ · Czechia"`.
 * - Uses `Intl.DisplayNames` (locale from UIProvider) when available.
 * - Falls back to the `name` supplied by `ReferenceDataProvider`.
 * - Renders the raw code alone if the code isn't recognized.
 */
export function CountryFormatter({ value, errors }: FormatterProps): ReactNode {
  const locale = useLocale();
  const code = value == null || value === '' ? '' : String(value).trim().toUpperCase();
  const match = useCountry(code);

  let text: string;
  if (!code) {
    text = '—';
  } else {
    let displayName: string | undefined;
    try {
      if (typeof Intl.DisplayNames === 'function') {
        const d = new Intl.DisplayNames([locale], { type: 'region' });
        const localized = d.of(code);
        if (localized && localized !== code) displayName = localized;
      }
    } catch {
      // fall through to ref-data lookup
    }
    displayName = displayName ?? match?.name;
    text = displayName ? `${code} · ${displayName}` : code;
  }

  return (
    <span className='inline-flex flex-col gap-2xs items-start'>
      <span>{text}</span>
      <FormatterError issues={errors} />
    </span>
  );
}
